import { Injectable, inject } from '@angular/core';
import type { CustomSpawnBracketIndex, CustomSpawnBracketRecord } from '@animus/type/domain';
import { CustomSpawnBracketRecordSchema } from '@animus/type/domain';
import type { CustomSpawnBracketPersistence } from '@animus/utility/web/custom-spawn-persistence';
import type { RxDocument } from 'rxdb/plugins/core';
import { map, type Observable } from 'rxjs';
import { RxDatabaseService } from './rxdb.service';

/**
 * RxDB-backed {@link CustomSpawnBracketPersistence}. Constructed through Angular DI: the database is injected via
 * {@link RxDatabaseService}, whose synchronous {@link RxDatabaseService.database} is set by the eager app
 * initializer. Tests construct the service and call `initialize()` first.
 */
@Injectable({ providedIn: 'root' })
export class RxDBCustomSpawnBracketPersistence implements CustomSpawnBracketPersistence {
    /** The application RxDB instance, ready once the eager app initializer resolves. */
    private readonly db = inject(RxDatabaseService).database;

    async list(): Promise<CustomSpawnBracketIndex> {
        const docs = await this.db['customSpawnBrackets'].find().exec();

        return toIndex(docs);
    }

    list$(): Observable<CustomSpawnBracketIndex> {
        return this.db['customSpawnBrackets'].find().$.pipe(map((docs) => toIndex(docs)));
    }

    async load(id: string): Promise<CustomSpawnBracketRecord | null> {
        const doc = await this.db['customSpawnBrackets'].findOne(id).exec();

        if (!doc) {
            return null;
        }

        return parseCurrentRecord(doc.toMutableJSON());
    }

    observe(id: string): Observable<CustomSpawnBracketRecord | null> {
        return this.db['customSpawnBrackets']
            .findOne(id)
            .$.pipe(map((doc) => (doc ? parseCurrentRecord(doc.toMutableJSON()) : null)));
    }

    async save(bracket: CustomSpawnBracketRecord): Promise<void> {
        const record = CustomSpawnBracketRecordSchema.parse(bracket);
        const existing = await this.db['customSpawnBrackets'].findOne(record.id).exec();

        if (!existing) {
            await this.db['customSpawnBrackets'].insert(record);

            return;
        }

        await existing.incrementalModify((value: unknown) => {
            const current = parseCurrentRecord(value);

            if (!current) {
                throw new Error(`Custom spawn bracket ${record.id} is not a recoverable ID-only record.`);
            }

            // Editing a bracket must never sever games that already reference it. `incrementalModify` retries
            // against the latest RxDB revision, so concurrent link/save operations union their relations instead
            // of letting a stale whole-document upsert clobber one of them.
            return {
                ...record,
                gameIds: uniqueSorted([...current.gameIds, ...record.gameIds]),
            };
        });
    }

    async linkToGame(bracketId: string, gameId: string): Promise<void> {
        const doc = await this.db['customSpawnBrackets'].findOne(bracketId).exec();

        if (!doc) {
            throw new Error(`Custom spawn bracket ${bracketId} is unavailable on this device.`);
        }

        if (!parseCurrentRecord(doc.toMutableJSON())) {
            throw new Error(`Custom spawn bracket ${bracketId} is not a recoverable ID-only record.`);
        }

        await doc.incrementalModify((value: unknown) => {
            const current = parseCurrentRecord(value);

            if (!current) {
                throw new Error(`Custom spawn bracket ${bracketId} is not a recoverable ID-only record.`);
            }

            if (current.gameIds.includes(gameId)) {
                return current;
            }

            return {
                ...current,
                gameIds: uniqueSorted([...current.gameIds, gameId]),
                updatedAt: new Date().toISOString(),
            };
        });
    }

    async delete(id: string): Promise<void> {
        const doc = await this.db['customSpawnBrackets'].findOne(id).exec();

        if (doc) {
            await doc.remove();
        }
    }
}

/** Validate + summarize a batch of bracket docs into the index shape, newest-updated first. */
function toIndex(docs: readonly RxDocument<CustomSpawnBracketRecord>[]): CustomSpawnBracketIndex {
    const brackets: CustomSpawnBracketIndex['brackets'] = [];

    for (const doc of docs) {
        const record = parseCurrentRecord(doc.toMutableJSON());

        if (record) {
            brackets.push({
                factionIds: record.factionIds,
                id: record.id,
                modelCount: record.pool.reduce((sum, entry) => sum + entry.count, 0),
                name: record.name ?? record.id,
                updatedAt: record.updatedAt,
            });
        }
    }

    brackets.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

    return { brackets };
}

/** Parses this release's exact ID-only record shape while ignoring only RxDB's own envelope metadata. */
function parseCurrentRecord(value: unknown): CustomSpawnBracketRecord | null {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    const { _attachments, _deleted, _meta, _rev, ...record } = value as Record<string, unknown>;

    const result = CustomSpawnBracketRecordSchema.safeParse(record);

    return result.success ? result.data : null;
}

function uniqueSorted(values: readonly string[]): string[] {
    return [...new Set(values)].toSorted();
}
